import { useMutation } from "@apollo/client";
import { MT_ADD_CONVERSATION } from "../graphQl/MessengerAPI";
import { useSendMessage } from "./useSendMessage";

export const useAddConversation = () => {
  const [addConversationMutation, { loading: creatingConversation, error: addConversationError }] =
    useMutation(MT_ADD_CONVERSATION);
  const { sendMessage, loading: sendingMessage, error: sendMessageError } = useSendMessage();

  const addConversation = async (conversationInput) => {
    const { data } = await addConversationMutation({ variables: { conversationInput } });
    return data?.MT_ADD_CONVERSATION?.data ?? null;
  };

  /**
   * Crée la conversation puis envoie le premier message dedans, en une seule opération pour
   * l'appelant. Ne redirige/ne réussit que si les DEUX étapes aboutissent :
   * - message vide (ou absent) -> erreur immédiate, aucune mutation appelée ;
   * - échec de la création -> erreur, pas d'envoi de message ;
   * - conversation créée mais envoi du premier message en échec -> erreur remontée telle quelle
   *   (la conversation existe déjà côté API à ce stade, mais on ne considère l'opération
   *   réussie, et on ne redirige, que lorsque le premier message est bien envoyé).
   */
  const addConversationWithMessage = async (conversationInput, message) => {
    const trimmedMessage = typeof message === "string" ? message.trim() : "";
    if (!trimmedMessage) {
      throw new Error("Le message ne peut pas être vide.");
    }

    const conversation = await addConversation(conversationInput);
    if (!conversation?.id) {
      throw new Error("La conversation n'a pas pu être créée.");
    }

    const sendResponse = await sendMessage({
      conversationId: conversation.id,
      content: trimmedMessage,
      sendingDate: new Date().toISOString(),
    });

    const sendResult = sendResponse?.data?.MT_SEND_MESSAGE;
    if (!sendResult?.data || sendResult?.error) {
      throw new Error(
        sendResult?.error?.message ||
          "La conversation a été créée mais l'envoi du message a échoué."
      );
    }

    return conversation;
  };

  return {
    addConversation,
    addConversationWithMessage,
    loading: creatingConversation || sendingMessage,
    error: addConversationError || sendMessageError,
  };
};
